import React, { FC, useMemo } from 'react';

import { EvFlexColumn, EvSection, EvSpinner } from '@evinced-private/ui-common';

import {
	MOBILE_SDK_TEST_ALL_ISSUES_TAB_DAID,
	MOBILE_SDK_TEST_ISSUES_TABLE_DAID
} from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import {
	MobileErrorMessage,
	MobileErrorType
} from 'src/apps/mobile-common/components/mobile-error-message/MobileErrorMessage';
import { useMobileSdkTestAllIssuesQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestAllIssuesQuery';
import { MobileSdkSingleTestFiltersSection } from 'src/apps/mobile-sdk/pages/single-test/filters-section/MobileSdkSingleTestFiltersSection';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { MOBILE_SDK_TEST_ALL_ISSUES_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import {
	getMobileSdkAllIssuesColumns,
	MOBILE_SDK_ALL_ISSUES_TABLE_OPTIONS
} from './MobileSdkTestAllIssuesTableHelper';

import './MobileSdkTestAllIssues.scss';

interface IMobileSdkTestAllIssuesProps {
	testId: string;
}

export const MobileSdkTestAllIssues: FC<IMobileSdkTestAllIssuesProps> = ({ testId }) => {
	const { getTableStateById, onFilterChange } = useTableState();
	const tableId = useMemo(
		() => buildFullTableId(MOBILE_SDK_TEST_ALL_ISSUES_TABLE_ID, testId),
		[testId]
	);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const issuesQuery = useMobileSdkTestAllIssuesQuery({
		testId,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		filters
	});

	if (issuesQuery?.isError) {
		return (
			<MobileErrorMessage
				errorType={MobileErrorType.LOADING_ERROR}
				productType={ProductType.MOBILE_SDK}
			/>
		);
	}

	if (!issuesQuery?.isLoading && !issuesQuery?.isPreviousData && !issuesQuery?.data) {
		return <EvSpinner />;
	}

	const { total = 0, unfilteredTotal = 0, rows = [] } = issuesQuery.data ?? {};

	return (
		<EvSection dataAutomationId={MOBILE_SDK_TEST_ALL_ISSUES_TAB_DAID}>
			<EvFlexColumn>
				<MobileSdkSingleTestFiltersSection
					testId={testId}
					filters={filters}
					ariaLabel="Mobile SDK Test's all issues filters"
					onFilterChange={onFilterChange}
					tableId={tableId}
				/>
			</EvFlexColumn>
			<EvSection ariaLabel="Mobile SDK Test's all issues table" skipLinkId="main-section">
				<EvFlexColumn margin={['MEDIUM', 'NONE', 'NONE', 'NONE']}>
					<div className="single-test-all-issues-table">
						<PlatformTable
							dataAutomationId={MOBILE_SDK_TEST_ISSUES_TABLE_DAID}
							isTableLoading={issuesQuery.isLoading || issuesQuery.isPreviousData}
							tableId={tableId}
							tableData={rows}
							totalCount={unfilteredTotal}
							totalPaginationableResults={total}
							persistTableState
							columns={getMobileSdkAllIssuesColumns(testId)}
							options={MOBILE_SDK_ALL_ISSUES_TABLE_OPTIONS}
							withUrlSwitcher={false}
						/>
					</div>
				</EvFlexColumn>
			</EvSection>
		</EvSection>
	);
};
